import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Shield,
  Loader,
  AlertCircle,
  Target,
  Briefcase,
  Wallet,
} from 'lucide-react';

import StatCard from './common/StatCard';
import SafeChart from './common/SafeChart';
import { CHART_COLORS as COLORS } from './common/Constants';

/**
 * Dashboard overview – combines save-status banner, KPI cards and enhanced charts.
 * Now includes auto-calculated metrics and improved visualizations.
 */
const OverviewTab = ({
  /* save banner */
  saving,
  lastSaved,
  saveUserData,
  error,
  /* stats */
  incomeData,
  netWorth,
  totalDebts,
  totalInvestments,
  emergencyFund,
  /* charts */
  netIncomeData,
  goals,
  investments,
  debts,
  expenseData,
  /* on-chain wallets */
  wallets = [],
  /* chartsReady flag from parent */
  chartsReady,
}) => {
  // Total crypto value across all connected wallets.
  // Uses manual override value when set, falls back to API-fetched value.
  const totalCryptoHoldings = wallets.reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );

  // Net worth includes wallet holdings on top of manual investments
  const autoNetWorth = totalInvestments + totalCryptoHoldings - totalDebts;
  const emergencyFundFromInvestments = investments
    .filter(inv => inv.type === 'Emergency Fund')
    .reduce((sum, inv) => sum + inv.amount, 0);
  
  // Calculate expense average for emergency fund target
  const calculateExpenseAverage = (months = 6) => {
    if (!expenseData.length) return 0;
    const recentExpenses = expenseData.slice(-months);
    const totalExpenses = recentExpenses.reduce((sum, row) => sum + (row.total || 0), 0);
    return recentExpenses.length > 0 ? totalExpenses / recentExpenses.length : 0;
  };

  const emergencyFundTarget = calculateExpenseAverage(6) * 6;
  const emergencyFundProgress = emergencyFundTarget ? (emergencyFundFromInvestments / emergencyFundTarget) * 100 : 0;

  // Prepare investment category data for pie chart
  const getInvestmentByCategory = () => {
    const categoryTotals = {};
    investments.forEach(inv => {
      const category = inv.type || 'Uncategorized';
      categoryTotals[category] = (categoryTotals[category] || 0) + (inv.amount || 0);
    });
    
    return Object.entries(categoryTotals)
      .map(([name, amount]) => ({ name, amount }))
      .filter(item => item.amount > 0)
      .sort((a, b) => b.amount - a.amount);
  };

  const investmentCategories = getInvestmentByCategory();

  // Get recent months data for trends
  const recentMonths = netIncomeData.slice(-6);

  // Filter goals for display (exclude auto-calculated ones or show calculated values)
  const displayGoals = goals.map(goal => {
    if (goal.name === 'FIRE Target') {
      return { ...goal, current: autoNetWorth, progress: goal.target ? (autoNetWorth / goal.target) * 100 : 0 };
    }
    if (goal.name === 'Emergency Fund') {
      return { 
        ...goal, 
        current: emergencyFundFromInvestments,
        target: emergencyFundTarget,
        progress: emergencyFundProgress
      };
    }
    return goal;
  });

  return (
    <div className="space-y-6">
      {/* Save Status */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div
          className={`flex-1 p-3 rounded-lg border ${
            saving ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {saving ? (
                <>
                  <Loader className="animate-spin text-yellow-600" size={16} />
                  <span className="text-sm text-yellow-800">Saving changes…</span>
                </>
              ) : (
                <>
                  <span className="text-green-600">✓</span>
                  <span className="text-sm text-green-800">
                    Auto‑saved {lastSaved ? lastSaved.toLocaleTimeString() : 'recently'}
                  </span>
                </>
              )}
            </div>

            <button
              onClick={saveUserData}
              disabled={saving}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
            >
              Save Now
            </button>
          </div>
        </div>

        {error && (
          <div className="flex-1 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-red-500" size={16} />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Latest Income"
          value={incomeData[incomeData.length - 1]?.total || 0}
          icon={DollarSign}
          color="green"
          subtitle="This month"
        />
        <StatCard
          title="Net Worth"
          value={autoNetWorth}
          icon={TrendingUp}
          color={autoNetWorth >= 0 ? "blue" : "red"}
          subtitle="Auto-calculated"
        />
        <StatCard
          title="Total Investments"
          value={totalInvestments}
          icon={Briefcase}
          color="purple"
          subtitle="Manual entries"
        />
        <StatCard
          title="Crypto Holdings"
          value={totalCryptoHoldings}
          icon={Wallet}
          color="yellow"
          subtitle={
            wallets.length === 0
              ? "No wallets yet"
              : `${wallets.length} wallet${wallets.length === 1 ? "" : "s"} connected`
          }
        />
        <StatCard
          title="Total Debt"
          value={totalDebts}
          icon={CreditCard}
          color="red"
          subtitle="Outstanding"
        />
        <StatCard
          title="Emergency Fund"
          value={emergencyFundFromInvestments}
          icon={Shield}
          color="yellow"
          subtitle={`${emergencyFundProgress.toFixed(0)}% of target`}
        />
      </div>

      {/* Financial Health Summary */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Financial Health Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Wealth Building */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
              autoNetWorth >= 1000000 ? 'bg-green-100' :
              autoNetWorth >= 100000 ? 'bg-blue-100' :
              autoNetWorth >= 0 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <TrendingUp className={`w-8 h-8 ${
                autoNetWorth >= 1000000 ? 'text-green-600' :
                autoNetWorth >= 100000 ? 'text-blue-600' :
                autoNetWorth >= 0 ? 'text-yellow-600' : 'text-red-600'
              }`} />
            </div>
            <h4 className="font-semibold mb-1">Wealth Status</h4>
            <p className="text-sm text-arc-muted">
              {autoNetWorth >= 1000000 ? '🚀 Millionaire' :
               autoNetWorth >= 500000 ? '💪 Half Millionaire' :
               autoNetWorth >= 100000 ? '📈 Six Figures' :
               autoNetWorth >= 0 ? '⚖️ Positive Net Worth' :
               '⚠️ Negative Net Worth'}
            </p>
          </div>

          {/* Emergency Preparedness */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
              emergencyFundProgress >= 100 ? 'bg-green-100' :
              emergencyFundProgress >= 50 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <Shield className={`w-8 h-8 ${
                emergencyFundProgress >= 100 ? 'text-green-600' :
                emergencyFundProgress >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`} />
            </div>
            <h4 className="font-semibold mb-1">Emergency Ready</h4>
            <p className="text-sm text-arc-muted">
              {emergencyFundProgress >= 100 ? '✅ Fully Prepared' :
               emergencyFundProgress >= 50 ? '⚠️ Partially Ready' :
               '❌ Needs Attention'}
            </p>
          </div>

          {/* Debt Status */}
          <div className="text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
              totalDebts === 0 ? 'bg-green-100' :
              totalDebts < 50000 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <CreditCard className={`w-8 h-8 ${
                totalDebts === 0 ? 'text-green-600' :
                totalDebts < 50000 ? 'text-yellow-600' : 'text-red-600'
              }`} />
            </div>
            <h4 className="font-semibold mb-1">Debt Status</h4>
            <p className="text-sm text-arc-muted">
              {totalDebts === 0 ? '🎉 Debt Free' :
               totalDebts < 50000 ? '📉 Manageable' :
               '⚠️ High Debt'}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Expenses Trend (Recent 6 months) */}
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Recent Income vs Expenses Trend</h3>
          <SafeChart chartsReady={chartsReady}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={recentMonths}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                <Legend />
                <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={3} name="Income" />
                <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={3} name="Expenses" />
                <Line type="monotone" dataKey="netIncome" stroke="#3b82f6" strokeWidth={3} name="Net Income" />
              </LineChart>
            </ResponsiveContainer>
          </SafeChart>
        </div>

        {/* Goal Progress with Auto-Updates */}
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Financial Goals Progress</h3>
          <div className="space-y-4">
            {displayGoals.slice(0, 4).map((goal) => (
              <div key={goal.name}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">{goal.name}</span>
                  <span className="text-sm text-arc-muted">{goal.progress.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-white/[0.08] rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${
                      goal.progress >= 100 ? 'bg-green-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${Math.min(goal.progress, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-arc-dim mt-1">
                  <span>${goal.current.toLocaleString()}</span>
                  <span>${goal.target.toLocaleString()}</span>
                </div>
                {(goal.name === 'FIRE Target' || goal.name === 'Emergency Fund') && (
                  <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs mt-1">
                    Auto-calculated
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Investment Portfolio by Category */}
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Investment Portfolio by Category</h3>
          <SafeChart chartsReady={chartsReady}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={investmentCategories}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  dataKey="amount"
                  nameKey="name"
                  fill="#8884d8"
                >
                  {investmentCategories.map((entry, i) => (
                    <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
              </PieChart>
            </ResponsiveContainer>
          </SafeChart>
          <div className="mt-4 text-center">
            <p className="text-sm text-arc-muted">
              Total Portfolio: <span className="font-semibold text-blue-600">${totalInvestments.toLocaleString()}</span>
            </p>
          </div>
        </div>

        {/* Debt Payoff Progress */}
        <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Debt Payoff Progress</h3>
          {debts.filter((d) => d.original > 0).length > 0 ? (
            <SafeChart chartsReady={chartsReady}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={debts.filter((d) => d.original > 0)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    fontSize={12}
                  />
                  <YAxis tickFormatter={(value) => `${value.toFixed(0)}%`} />
                  <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Paid Off']} />
                  <Bar dataKey="paidOff" name="Paid Off Percentage">
                    {debts.filter((d) => d.original > 0).map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.paidOff >= 100 ? '#10b981' : COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </SafeChart>
          ) : (
            <div className="flex items-center justify-center h-[300px] bg-green-50 rounded-lg">
              <div className="text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h4 className="text-lg font-semibold text-green-800 mb-2">Debt Free!</h4>
                <p className="text-sm text-green-600">You have no outstanding debts</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-arc-elevated p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold mb-4">Quick Insights & Next Steps</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Investment Insight */}
          <div className="p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">💰 Investment Focus</h4>
            <p className="text-sm text-arc-muted">
              {emergencyFundProgress < 100 
                ? `Build emergency fund first. Need $${(emergencyFundTarget - emergencyFundFromInvestments).toLocaleString()} more.`
                : totalInvestments < 100000
                ? "Emergency fund complete! Focus on growing investments to $100k milestone."
                : "Great progress! Consider diversifying across different categories."
              }
            </p>
          </div>

          {/* Debt Insight */}
          <div className="p-4 bg-red-50 rounded-lg">
            <h4 className="font-semibold text-red-800 mb-2">💳 Debt Strategy</h4>
            <p className="text-sm text-arc-muted">
              {totalDebts === 0
                ? "Excellent! You're debt-free. Redirect payments to investments."
                : totalDebts > totalInvestments
                ? "Consider prioritizing high-interest debt before investing more."
                : "Good balance. Continue paying down debt while building wealth."
              }
            </p>
          </div>

          {/* Net Worth Insight */}
          <div className="p-4 bg-green-50 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">📈 Wealth Building</h4>
            <p className="text-sm text-arc-muted">
              {autoNetWorth < 0
                ? "Focus on debt reduction and emergency fund building first."
                : autoNetWorth < 100000
                ? `You're ${((100000 - autoNetWorth) / 1000).toFixed(0)}k away from six-figure net worth!`
                : autoNetWorth < 1000000
                ? `You're ${((1000000 - autoNetWorth) / 1000).toFixed(0)}k away from millionaire status!`
                : "Congratulations on reaching millionaire status! 🎉"
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;